import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, Loader2, Search, User, FileText, Settings, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuditLog, useMatterPartyNames, getPartyName } from "@/hooks/use-supabase-queries";
import { UpgradeNotice } from "@/components/subscription/UpgradeNotice";
import { useSubscription } from "@/hooks/useSubscription";

const typeConfig: Record<string, { icon: typeof FileText; className: string }> = {
  document: { icon: FileText, className: "bg-primary/10 text-primary" },
  matter_stage: { icon: CheckCircle2, className: "bg-accent/10 text-accent" },
  deal: { icon: Settings, className: "bg-secondary text-secondary-foreground" },
  default: { icon: Settings, className: "bg-muted text-muted-foreground" },
};

interface MatterAuditLogProps {
  matterId: string;
}

export function MatterAuditLog({ matterId }: MatterAuditLogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const { entitlements } = useSubscription();
  const { data: entries, isLoading } = useAuditLog(matterId);
  const { data: partyNames } = useMatterPartyNames(matterId);

  const filteredLog = useMemo(() => {
    if (!entitlements.auditSearch || !searchQuery.trim()) {
      return entries || [];
    }

    return (entries || []).filter(
      (entry) =>
        entry.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (entry.details || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        getPartyName(partyNames, entry.user_id).toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [entitlements.auditSearch, entries, partyNames, searchQuery]);

  if (isLoading) {
    return (
      <Card><CardContent className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </CardContent></Card>
    );
  }

  const handleExport = () => {
    const rows = filteredLog.map((entry) => ({
      action: entry.action,
      entity_type: entry.entity_type || 'system',
      performed_by: getPartyName(partyNames, entry.user_id),
      details: (entry.details || '').replace(/\r?\n/g, ' '),
      created_at: format(new Date(entry.created_at), 'yyyy-MM-dd HH:mm:ss'),
    }));

    const header = ['Action', 'Entity Type', 'Performed By', 'Details', 'Created At'];
    const csv = [
      header.join(','),
      ...rows.map((row) =>
        [row.action, row.entity_type, row.performed_by, row.details, row.created_at]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `matter-audit-log-${matterId}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {!entitlements.auditSearch ? (
        <UpgradeNotice
          title="Search and export require Professional"
          description="Starter includes a basic audit trail, while Professional and Enterprise unlock search, filtering, and CSV export."
        />
      ) : null}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Audit Log</CardTitle>
              <p className="text-sm text-muted-foreground">Complete history of all actions on this matter</p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={entitlements.auditSearch ? "Search audit log..." : "Search unlocks on Professional"}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  disabled={!entitlements.auditSearch}
                />
              </div>
              {entitlements.auditExport ? (
                <Button variant="outline" onClick={handleExport} disabled={filteredLog.length === 0}>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredLog.length === 0 ? (
            <div className="py-8 text-center">
              <Search className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">{entries?.length === 0 ? 'No audit entries yet' : 'No matching entries found'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredLog.map((entry, index) => {
                const config = typeConfig[entry.entity_type || 'default'] || typeConfig.default;
                const Icon = config.icon;
                return (
                  <div key={entry.id} className="relative">
                    {index < filteredLog.length - 1 && (
                      <div className="absolute left-5 top-12 h-full w-0.5 bg-border" />
                    )}
                    <div className="flex gap-4">
                      <div className={`z-10 rounded-full p-2 ${config.className}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1 pb-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-foreground">{entry.action.replace(/_/g, ' ')}</p>
                            {entry.details ? <p className="text-sm text-muted-foreground">{entry.details}</p> : null}
                          </div>
                          <Badge variant="outline" className="flex-shrink-0">{entry.entity_type || 'system'}</Badge>
                        </div>
                        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {getPartyName(partyNames, entry.user_id)}
                          </span>
                          <span>{format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
